import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styles from '../styles.scss';
import StravaPhotoLightbox from '../StravaPhotoLightbox';

import { photoListProps } from '../../../../props/feed-prop-types';
import { useFeedEntityContext } from '../../../../state/FeedEntityContext';
import useFeedAnalytics from '../../../../hooks/useFeedAnalytics';

const Poc = React.lazy(() => import('./Poc'));

// photo image entries and their associated tags
const PhotoGrid = ({
  photoList,
  maxNumPhotosToShow,
  singleImageHighlighted,
  entryTypeClassName
}) => {
  const { entityState, analyticProps } = useFeedEntityContext();
  const [params, setParams] = useState({
    isOpen: false,
    photoIndex: 0
  });

  const { intersectionObserverRef } = useFeedAnalytics({
    ...analyticProps,
    element: 'image'
  });

  const photoCount = photoList.length;
  const showPhotoLightbox = params.isOpen && photoCount > 0;

  const openPhotoLightBox = (index) => {
    setParams({ isOpen: true, photoIndex: index });
  };

  const snowplowImageClickEvent = () => {
    entityState.trackAnalytics({
      action: 'click',
      element: 'image'
    });
  };

  const photoGrid = () => {
    const photoGridElements = [];
    if (singleImageHighlighted) {
      // Single image grid
      // Only the first photo (highlighted image) is displayed on posts
      // Image on posts do not open the lightbox
      const highlightedImageForPosts = photoList[0];
      photoGridElements.push(
        <div
          className={`${entryTypeClassName} ${styles.entryImage}`}
          data-testid="post"
          key="single_image_grid"
          ref={intersectionObserverRef}
        >
          <div className={styles.entryImageWrapper}>
            <img
              src={highlightedImageForPosts.large}
              srcSet={highlightedImageForPosts.large}
              alt={highlightedImageForPosts.caption_escaped}
            />
          </div>
        </div>
      );
    } else {
      // Multiple photos grid
      photoGridElements.push(
        photoList.slice(0, maxNumPhotosToShow).map((photo, index) => {
          if (photo?.media_type === 2) {
            return (
              <div
                data-testid="photo"
                key={photo.photo_id}
                className={styles.entryImage}
                ref={intersectionObserverRef}
              >
                <React.Suspense fallback={<div />}>
                  <Poc className={styles.entryImageWrapper} media={photo} />
                </React.Suspense>
              </div>
            );
          }

          const { photo_id: photoId, large, caption_escaped: caption } = photo;

          return (
            <div
              data-testid="photo"
              key={photoId}
              className={styles.entryImage}
              ref={intersectionObserverRef}
            >
              <div
                className={styles.entryImageWrapper}
                onClick={() => {
                  snowplowImageClickEvent();
                  openPhotoLightBox(index);
                }}
                onKeyPress={() => {
                  snowplowImageClickEvent();
                  openPhotoLightBox(index);
                }}
                role="button"
                tabIndex="0"
              >
                {/* Enhanced Partner Tag */}
                {photo.showEnhancedTag && photo.enhanced_photo && (
                  <div data-testid="partner_tag" className={styles.enhancedTag}>
                    {photo.enhanced_photo.name}
                  </div>
                )}
                <img src={large} alt={caption} />
              </div>
            </div>
          );
        })
      );
      // show count for more images eg. +5 indicating that there
      // are 5 more images uploaded by the athlete that are not being currently shown on the
      // photo grid.
      if (photoCount > maxNumPhotosToShow) {
        const extraPhotos = photoCount - maxNumPhotosToShow;
        photoGridElements.push(
          <div
            key="more_images"
            className={styles.entryMoreImages}
            onClick={() => snowplowImageClickEvent()}
            onKeyPress={() => snowplowImageClickEvent()}
            role="button"
            tabIndex="0"
            ref={intersectionObserverRef}
          >
            <div className={styles.moreTextWrapper}>
              <div className={styles.moreText}>
                <div data-testid="more-text">{`+ ${extraPhotos}`}</div>
              </div>
            </div>
          </div>
        );
      }
    }
    // Add photo lightbox
    if (showPhotoLightbox) {
      photoGridElements.push(
        <StravaPhotoLightbox
          key="photo_lightbox"
          selectedPhotoIndex={params.photoIndex}
          photoList={photoList}
          onCloseRequest={() => {
            setParams({ isOpen: false });
            entityState.trackAnalytics({
              action: 'screen_exit',
              element: 'image'
            });
          }}
        />
      );
    }

    return photoGridElements;
  };

  return photoGrid();
};

PhotoGrid.propTypes = {
  photoList: photoListProps.isRequired,
  maxNumPhotosToShow: PropTypes.number,
  singleImageHighlighted: PropTypes.bool,
  entryTypeClassName: PropTypes.oneOf([
    styles.activityMap,
    styles.groupActivityMap,
    styles.postImage
  ]).isRequired
};

PhotoGrid.defaultProps = {
  maxNumPhotosToShow: 1,
  singleImageHighlighted: false
};

export default PhotoGrid;
